import React, { FunctionComponent, useState } from "react";
import {
  Badge,
  Box,
  Card,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  SwipeableDrawer,
  useTheme
} from "@mui/material";
import { strings } from "../../localization/LocalizedStrings";
import { useNavigate } from "react-router";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { PreferencesDialog } from "../PreferencesDialog";
import { AlertDialog } from "../AlertDialog";
import { DiscogsImportDialog } from "../DiscogsImportDialog";
import { CollectionsDialog } from "../collections/CollectionsDialog";
import styles from "./styles.module.scss";
import { UserAvatar } from "../UserAvatar";
import { DiscreetLink } from "../DiscreetLink";
import { useSessionManager, useUser, useUserProfile } from "../../hooks/session";
import { MobileButtons } from "../MobileButtons";
import { ExcelImportDialog } from "../ExcelImportDialog";
import { useIsScreenWide } from "../../hooks/isScreenWide";
import { DrawerHeader } from "../DrawerHeader";
import { BuyMeACoffeeButton } from "../BuyMeACoffeeButton";
import { productIconMap } from "../../models/ProductUtilities";
import { ProductType } from "../../hooks/products/interfaces";
import { Icon, IconType } from "../../icons";
import { PiperDivider } from "../PiperDivider";

export const MainDrawer: FunctionComponent<IMainDrawerProps> = ({
  currentPage,
  isOpen,
  setIsOpen,
  isLayout,
  isOpenDiscogsImport,
  setIsOpenDiscogsImport,
  isOpenExcelImport,
  setIsOpenExcelImport,
  onFilterAlbumsClicked,
  onRandomAlbumsClicked,
  onNewAlbumClicked,
  onNewBoxSetClicked,
  onBarcodeScanClicked
}) => {
  const theme = useTheme();
  const isScreenWide = useIsScreenWide();
  const navigate = useNavigate();
  const [currentUser] = useUser.useState();
  const [currentUserProfile] = useUserProfile.useState();
  const { closeSession } = useSessionManager();
  const [isOpenManageCollectionsDialog, setIsOpenManageCollectionsDialog] = useState(false);
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false);
  const [isReorderAlertOpen, setIsReorderAlertOpen] = useState(false);
  const [isStatsGuestAlertOpen, setIsStatsGuestAlertOpen] = useState(false);
  const list = () => (
    <Box
      sx={{
        width: 310,
        height: "100%",
        overflow: "auto",
        backgroundColor: theme.palette.colors?.drawerBackgroundColor
      }}
      role="presentation"
    >
      <List className={styles.list}>
        <Card className={styles.sectionCard}>
          <DiscreetLink
            to={RoutesBuilder.userProfile()}
            disabled={
              currentUser === undefined ||
              currentUserProfile === undefined ||
              currentUser?.role == "guest"
            }
          >
            <ListItemButton
              key={"User"}
              className={styles.listItem}
              selected={currentPage == "profile"}
            >
              <ListItemAvatar style={{ minWidth: "48px" }}>
                <Badge
                  overlap={"circular"}
                  badgeContent={currentUserProfile?.unreadNotifications}
                  color={"error"}
                >
                  <UserAvatar className={styles.avatar} userProfile={currentUserProfile} />
                </Badge>
              </ListItemAvatar>
              {currentUserProfile ? (
                <ListItemText
                  disableTypography
                  className={styles.usernameTitle}
                  primary={
                    currentUser?.role != "guest" ? currentUserProfile.username : strings.guest_title
                  }
                />
              ) : (
                <Skeleton variant={"text"} width={"100%"} height={20} />
              )}
            </ListItemButton>
          </DiscreetLink>
          {currentUser?.role == "guest" && <PiperDivider />}
          {currentUser?.role == "guest" && (
            <ListItemButton
              key={"NewUser"}
              className={styles.listItem}
              onClick={() => {
                closeSession();
                navigate(RoutesBuilder.signIn());
              }}
            >
              <ListItemIcon>
                <Icon type={IconType.PersonAdd} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.login_create_user_or_login}
              />
            </ListItemButton>
          )}
        </Card>
        <Card className={styles.sectionCard}>
          <DiscreetLink to={RoutesBuilder.albums.list()}>
            <ListItemButton
              key={"Albums"}
              className={styles.listItem}
              selected={currentPage == "albums"}
            >
              <ListItemIcon>
                <Icon type={IconType.CardsStar} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.albums_title}
              />
            </ListItemButton>
          </DiscreetLink>
          <PiperDivider />
          <DiscreetLink to={RoutesBuilder.layout()}>
            <ListItemButton
              key={"Layout"}
              className={styles.listItem}
              selected={currentPage == "layout"}
            >
              <ListItemIcon>
                <Icon type={productIconMap[ProductType.SHELVINGS]} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.layout_title}
              />
            </ListItemButton>
          </DiscreetLink>
          <PiperDivider />
          <ListItemButton
            key={"Collections"}
            className={styles.listItem}
            onClick={() => {
              setIsOpenManageCollectionsDialog(true);
            }}
          >
            <ListItemIcon>
              <Icon type={productIconMap[ProductType.COLLECTIONS]} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.collections_manage_title}
            />
          </ListItemButton>
          <PiperDivider />
          <DiscreetLink to={RoutesBuilder.attributes.manage()}>
            <ListItemButton
              key={"Attributes"}
              className={styles.listItem}
              selected={currentPage == "attributes"}
            >
              <ListItemIcon>
                <Icon type={IconType.Assignment} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.option_attributes}
              />
            </ListItemButton>
          </DiscreetLink>
          <PiperDivider />
          <DiscreetLink to={currentUser?.role == "guest" ? undefined : RoutesBuilder.stats()}>
            <ListItemButton
              key={"Stats"}
              className={styles.listItem}
              selected={currentPage == "stats"}
              onClick={() => {
                if (currentUser?.role == "guest") {
                  setIsStatsGuestAlertOpen(true);
                }
              }}
            >
              <ListItemIcon>
                <Icon type={IconType.AreaChart} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.option_stats}
              />
            </ListItemButton>
          </DiscreetLink>
          <PiperDivider />
          <DiscreetLink to={RoutesBuilder.marketplace()}>
            <ListItemButton
              key={"Marketplace"}
              className={styles.listItem}
              selected={["marketplace", "publicAlbums"].includes(currentPage)}
            >
              <ListItemIcon>
                <Icon type={IconType.Public} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.option_marketplace}
              />
            </ListItemButton>
          </DiscreetLink>
        </Card>
        {currentPage == "albums" && (
          <Card className={styles.sectionCard}>
            <div>
              <ListItemButton
                key={"NewAlbum"}
                className={styles.listItem}
                onClick={() => onNewAlbumClicked()}
              >
                <ListItemIcon>
                  <Icon type={IconType.AddAd} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.ca_title}
                />
              </ListItemButton>
              <PiperDivider />
              <ListItemButton
                key={"BarcodeScan"}
                className={styles.listItem}
                onClick={() => {
                  if (onBarcodeScanClicked) {
                    onBarcodeScanClicked();
                  }
                }}
              >
                <ListItemIcon>
                  <Icon type={IconType.QRCode2Add} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.option_scan_barcode}
                />
              </ListItemButton>
              <PiperDivider />
              <ListItemButton
                key={"NewBoxSet"}
                className={styles.listItem}
                onClick={() => onNewBoxSetClicked()}
              >
                <ListItemIcon>
                  <Icon type={IconType.BoxAdd} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.new_box_set_title}
                />
              </ListItemButton>
              <PiperDivider />
              <ListItemButton
                key={"Reorder"}
                className={styles.listItem}
                onClick={() => setIsReorderAlertOpen(true)}
              >
                <ListItemIcon>
                  <Icon type={IconType.FormatListNumbered} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.option_reorder_albums}
                />
              </ListItemButton>
              <PiperDivider />
              <ListItemButton
                key={"Filter"}
                className={styles.listItem}
                onClick={() => {
                  if (onFilterAlbumsClicked) {
                    onFilterAlbumsClicked();
                  }
                }}
              >
                <ListItemIcon>
                  <Icon type={IconType.FilterAlt} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.option_filter_albums}
                />
              </ListItemButton>
              <PiperDivider />
              <ListItemButton
                key={"Random"}
                className={styles.listItem}
                onClick={() => {
                  if (onRandomAlbumsClicked) {
                    onRandomAlbumsClicked();
                  }
                }}
              >
                <ListItemIcon>
                  <Icon type={IconType.Die} />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  className={styles.itemTitle}
                  primary={strings.option_random_albums}
                />
              </ListItemButton>
            </div>
          </Card>
        )}
        <Card className={styles.sectionCard}>
          <ListItemButton
            key={"DiscogsImport"}
            className={styles.listItem}
            onClick={() => {
              setIsOpenDiscogsImport(true);
            }}
          >
            <ListItemIcon>
              <Icon type={IconType.CloudDownload} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.option_discogs_import}
            />
          </ListItemButton>
          <PiperDivider />
          <ListItemButton
            key={"ExcelImport"}
            className={styles.listItem}
            onClick={() => {
              setIsOpenExcelImport(true);
            }}
          >
            <ListItemIcon>
              <Icon type={IconType.FormsAddOn} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.option_excel_import}
            />
          </ListItemButton>
        </Card>
        <Card className={styles.sectionCard}>
          <ListItemButton
            key={"Preferences"}
            className={styles.listItem}
            onClick={() => {
              setIsPreferencesDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <Icon type={IconType.Settings} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.option_preferences}
            />
          </ListItemButton>
          <PiperDivider />
          <DiscreetLink to={RoutesBuilder.documentation.home()}>
            <ListItemButton key={"Documentation"} className={styles.listItem}>
              <ListItemIcon>
                <Icon type={IconType.Book2} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={strings.documentation}
              />
            </ListItemButton>
          </DiscreetLink>
          <PiperDivider />
          <ListItemButton
            key={"CloseSession"}
            className={styles.listItem}
            onClick={() => {
              closeSession();
              navigate(RoutesBuilder.signIn());
            }}
          >
            <ListItemIcon>
              <Icon type={IconType.Logout} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              className={styles.itemTitle}
              primary={strings.option_close_session}
            />
          </ListItemButton>
        </Card>
        <MobileButtons />
        <BuyMeACoffeeButton />
        {currentUser?.role == "admin" && (
          <Card className={styles.sectionCard} style={{ marginTop: 200 }}>
            <ListItemButton
              key={"admin"}
              className={styles.listItem}
              onClick={() => navigate(RoutesBuilder.admin())}
            >
              <ListItemIcon>
                <Icon type={IconType.Hardware} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                className={styles.itemTitle}
                primary={"Admin console"}
              />
            </ListItemButton>
          </Card>
        )}
      </List>
    </Box>
  );
  return (
    <div>
      <SwipeableDrawer
        anchor={"left"}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        variant={isScreenWide ? "persistent" : "temporary"}
      >
        <DrawerHeader setIsOpen={setIsOpen} />
        {list()}
      </SwipeableDrawer>
      <CollectionsDialog
        open={isOpenManageCollectionsDialog}
        setOpen={setIsOpenManageCollectionsDialog}
        isLayout={isLayout}
        canModifyCollections={true}
        displayStandardCollections={false}
        selectedCollections={[]}
        forceLoading={false}
        canNegate={false}
      />
      <PreferencesDialog
        isOpen={isPreferencesDialogOpen}
        setIsOpen={setIsPreferencesDialogOpen}
        canRemoveAccount={false}
      />
      <DiscogsImportDialog
        isOpen={isOpenDiscogsImport}
        setIsOpen={setIsOpenDiscogsImport}
        isLayout={isLayout}
      />
      <ExcelImportDialog
        isOpen={isOpenExcelImport}
        setIsOpen={setIsOpenExcelImport}
        isLayout={isLayout}
      />
      <AlertDialog
        message={strings.reorder_tutorial}
        open={isReorderAlertOpen}
        setOpen={setIsReorderAlertOpen}
        helpLink={`${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.orderAlbums()}`}
        hasCloseButton
      />
      <AlertDialog
        message={strings.stats_guest_error}
        open={isStatsGuestAlertOpen}
        setOpen={setIsStatsGuestAlertOpen}
        hasCloseButton
      />
    </div>
  );
};

export interface IMainDrawerProps {
  currentPage:
    | "albums"
    | "attributes"
    | "stats"
    | "marketplace"
    | "publicAlbums"
    | "profile"
    | "listeningSessions"
    | "layout";
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isLayout: boolean;
  isOpenDiscogsImport: boolean;
  setIsOpenDiscogsImport: (isOpen: boolean) => void;
  isOpenExcelImport: boolean;
  setIsOpenExcelImport: (isOpen: boolean) => void;
  onFilterAlbumsClicked?: () => void;
  onRandomAlbumsClicked?: () => void;
  onNewAlbumClicked?: () => void;
  onNewBoxSetClicked?: () => void;
  onBarcodeScanClicked?: () => void;
}
